import { FC, ReactNode, useCallback, useMemo } from 'react';
import { Checkbox, CheckboxChangeEvent } from 'antd';
import styled from 'styled-components';
import { useFormTableErrors } from './TanstackFormTable';
import { i18nNsFactory } from '@atlas-frontend/i18n';
import translations from './WithErrorRowsCheckbox.translations.json';
import { colorGray } from '@atlas-frontend/design-tokens';
import { useSearchParams } from 'react-router-dom';

const { addTranslations, useTranslation } = i18nNsFactory('error-rows-filter');
addTranslations(translations);

interface WithErrorRowsCheckboxProps {
  columnId: string;
  children: ReactNode;
}

export const WithErrorRowsCheckbox: FC<WithErrorRowsCheckboxProps> = ({
  columnId,
  children,
}) => {
  const { t } = useTranslation();
  const errorInfo = useFormTableErrors();
  const errorCount = errorInfo?.columnErrorCounts.get(columnId) || 0;

  const [searchParams, setSearchParams] = useSearchParams();
  const errorFilterColumns = useMemo(() => {
    return searchParams.get('errorFilterColumns')?.split(',') || [];
  }, [searchParams]);
  const isChecked =
    errorFilterColumns.some((column) => column === columnId) || false;

  const handleChange = useCallback(
    (e: CheckboxChangeEvent) => {
      let columns: String[] = [];
      if (e.target.checked) {
        columns = errorFilterColumns.concat(columnId);
      } else {
        const index = errorFilterColumns.indexOf(e.target.value);
        if (index !== -1) {
          columns = errorFilterColumns.splice(index, 1);
        }
      }

      setSearchParams((params) => {
        const newParams = new URLSearchParams(params);
        if (columns.length === 0) {
          newParams.delete('errorFilterColumns');
        } else {
          newParams.set('errorFilterColumns', columns.join(','));
        }
        return newParams;
      });
    },
    [columnId, errorFilterColumns, setSearchParams],
  );

  // Prevent mousedown from causing blur/closing popover
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
  }, []);

  if (!errorInfo || errorCount === 0) return <>{children}</>;

  return (
    <div>
      <ErrorRowsFilterWrapper onMouseDown={handleMouseDown}>
        <Checkbox
          id={`error-rows-checkbox-${columnId}`}
          checked={isChecked}
          onChange={handleChange}
        >
          <span>{t('label')}</span>
        </Checkbox>
      </ErrorRowsFilterWrapper>
      {children}
    </div>
  );
};

const ErrorRowsFilterWrapper = styled.div`
  padding-bottom: 8px;
  margin-bottom: 8px;
  border-bottom: 1px solid ${colorGray[1]};
`;
